import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { RecruitingCheckoutButton } from "@/components/Pricing";
import Header from "@/components/Header";
import AdminNav from "@/components/AdminNav";
import { useAuth } from "@/lib/auth";
import { Plus, Briefcase, Users, BarChart3, MessageSquare, Shield, Megaphone, Heart, Sparkles, Trophy } from "lucide-react";

export default function ADDashboard() {
  const { user } = useAuth();
  const [program, setProgram] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobQuery, setJobQuery] = useState("");
  const filteredJobs = jobQuery.trim()
    ? jobs.filter((j) => [j.title, j.program_name, j.location, j.region, j.division, j.level, j.gender, j.role]
        .filter(Boolean).join(" ").toLowerCase().includes(jobQuery.trim().toLowerCase()))
    : jobs;
  const [intel, setIntel] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [nationalRank, setNationalRank] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/program/me").catch(() => ({ data: null })),
      api.get("/jobs"),
      api.get("/market/intel").catch(() => ({ data: null })),
      api.get("/subscriptions/me").catch(() => ({ data: [] })),
      api.get("/program/national-rank").catch(() => ({ data: null })),
    ]).then(([p, j, i, s, r]) => {
      setProgram(p.data);
      setJobs(j.data);
      setIntel(i.data);
      setNationalRank(r.data?.ranked ? r.data : null);
      // Pick the first active program/AD subscription (comp_grant takes priority)
      const subs = Array.isArray(s.data) ? s.data : [];
      const comped = subs.find((x) => x.comp_grant && x.active !== false);
      const active = comped || subs.find((x) => x.active !== false) || null;
      setSubscription(active);
      setLoading(false);
    });
  }, []);

  if (loading) return <Shell><div className="p-10 heading text-3xl">LOADING…</div></Shell>;

  const isFoundingPartner = !!subscription?.comp_grant;
  const isFoundingMember = !!subscription?.founding_member && !subscription?.comp_grant;
  const compedUntil = isFoundingPartner && subscription.comped_until
    ? new Date(subscription.comped_until).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;

  return (
    <Shell>
      <div className="px-4 md:px-8 lg:px-12 py-8" data-testid="ad-dashboard">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <div className="text-xs tracking-widest text-volt">PROGRAM CONTROL ROOM</div>
            {program ? (
              <>
                <h1 className="heading text-4xl md:text-5xl lg:text-6xl mt-2" data-testid="ad-program-name">{(program.name || "").toUpperCase()}</h1>
                <p className="text-neutral-400 mt-2 text-sm">{program.gender} {program.sport} · {program.level}{program.location ? ` · ${program.location}` : ""}</p>
                {nationalRank && (
                  <div
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-volt/10 border border-volt text-volt text-xs tracking-widest heading"
                    data-testid="national-rank-badge"
                  >
                    <Trophy size={14} strokeWidth={2} />
                    #{nationalRank.rank} NATIONALLY · {nationalRank.peer_group.toUpperCase()}
                    {nationalRank.rank / nationalRank.total <= 0.5 ? ` · TOP ${Math.max(1, Math.ceil((nationalRank.rank / nationalRank.total) * 100))}%` : ""}
                  </div>
                )}
              </>
            ) : (
              <>
                <h1 className="heading text-4xl md:text-5xl lg:text-6xl mt-2" data-testid="ad-program-name-missing">PROGRAM PROFILE PENDING</h1>
                <p className="text-neutral-400 mt-2 text-sm">
                  We couldn&apos;t find a program record on your account. Head over to <Link to="/ad/jobs/new" className="text-blaze underline">Post Job</Link> to seed one, or contact <a className="text-blaze underline" href="mailto:founders@coachconnect.ai">founders@coachconnect.ai</a> if this looks wrong.
                </p>
              </>
            )}
            {isFoundingPartner && (
              <div
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blaze/10 border border-blaze text-blaze text-xs tracking-widest heading"
                data-testid="founding-partner-badge"
              >
                <Heart size={14} strokeWidth={2} fill="currentColor" />
                FOUNDING PARTNER · COMPLIMENTARY ACCESS{compedUntil ? ` · THROUGH ${compedUntil.toUpperCase()}` : ""}
              </div>
            )}
            {isFoundingMember && (
              <div
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-volt/10 border border-volt text-volt text-xs tracking-widest heading"
                data-testid="founding-member-badge"
              >
                <Sparkles size={14} strokeWidth={2} />
                FOUNDING MEMBER
              </div>
            )}
          </div>
          <div className="lg:col-span-4 border border-neutral-800 p-6 bg-neutral-950" data-testid="north-star-card">
            <div className="text-xs tracking-widest text-blaze">NORTH STAR</div>
            <div className="heading text-6xl text-mono mt-2">{jobs.filter((j) => j.status === "open").length}</div>
            <div className="text-neutral-400 text-xs mt-1">ACTIVE SEARCHES</div>
            <div className="mt-3 text-xs text-neutral-500">{intel?.total_coaches ?? 0} coaches in TKG · {intel?.total_openings ?? 0} live openings network-wide</div>
            {intel ? (
              <div className="mt-4 text-xs text-volt tracking-widest" data-testid="ad-recruiting-active">PROGRAM RECRUITING · ACTIVE</div>
            ) : (
              <div className="mt-4" data-testid="ad-recruiting-cta">
                <div className="text-xs text-neutral-400 mb-2">Proactive talent search, matching and market intel — $5/month per program.</div>
                <RecruitingCheckoutButton className="w-full justify-center" label="START PROGRAM RECRUITING — $5/MO" />
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-6 gap-4">
          <Action to="/ad/jobs/new" icon={Plus} label="Post Job" sub="ProgramNeedsAgent guided" testid="action-post-job" />
          <Action to="/ad/stealth" icon={Shield} label="Stealth Search" sub="Anonymous match · NDA-gated" testid="action-stealth" />
          <Action to="/ad/talent" icon={Users} label="Talent Pool" sub="Browse scouted coaches" testid="action-talent" />
          {user?.is_platform_admin && (
            <Action to="/ad/marketing" icon={Megaphone} label="Growth" sub="Platform admin · funnel + outreach" testid="action-marketing" admin />
          )}
          <Action to="/ad/market" icon={BarChart3} label="Market Intel" sub="Live salary benchmarks" testid="action-market" />
          <Action to="/messages" icon={MessageSquare} label="Messages" sub="Candidate conversations" testid="action-messages" />
        </div>

        {/* Jobs */}
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs tracking-widest text-blaze">YOUR POSTINGS</div>
              <div className="heading text-3xl mt-1">ACTIVE SEARCHES</div>
            </div>
            <Link to="/ad/jobs/new" data-testid="post-job-link" className="bg-blaze text-white heading text-base px-5 py-2.5 hover:glow-blaze transition-all flex items-center gap-2">
              <Plus size={16} /> NEW JOB
            </Link>
          </div>
          <input
            data-testid="jobs-search-input"
            value={jobQuery}
            onChange={(e) => setJobQuery(e.target.value)}
            placeholder="Search jobs — program, title, location, division…"
            className="w-full md:w-96 mb-4 bg-[#121212] border border-neutral-700 focus:border-blaze outline-none px-3 py-2.5 text-sm text-white placeholder-neutral-600"
          />
          {filteredJobs.length === 0 ? (
            <div className="border border-dashed border-neutral-800 p-10 text-neutral-500" data-testid="jobs-empty">
              {jobQuery ? `No jobs match "${jobQuery}".` : "No postings yet. Create your first search."}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredJobs.map((j) => (
                <Link
                  key={j.id}
                  to={`/jobs/${j.id}`}
                  data-testid={`job-card-${j.id}`}
                  className="border border-neutral-800 bg-appcard p-5 hover:border-volt transition-colors block"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs text-neutral-500 tracking-widest">{j.gender?.toUpperCase()} · {j.sport?.toUpperCase()} · {j.division || j.level}</div>
                      <div className="heading text-xl mt-1">{j.title}</div>
                      <div className="text-neutral-400 text-sm">{j.location || j.region} · {j.salary_band ? `$${j.salary_band}` : ""}</div>
                    </div>
                    <Briefcase className="text-volt" size={20} strokeWidth={1.5} />
                  </div>
                  <div className="mt-3 text-sm text-neutral-300 line-clamp-2">{j.description}</div>
                  <div className="mt-3 text-xs text-mono text-blaze">VIEW MATCHES →</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}

function Action({ to, icon: Icon, label, sub, testid, admin }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      title={admin ? "Admin-only (back office)" : undefined}
      className={`border p-5 transition-colors group block ${
        admin ? "border-amber-500/50 bg-amber-950/20 hover:border-amber-400" : "border-neutral-800 bg-appcard hover:border-blaze"
      }`}
    >
      <Icon className={admin ? "text-amber-400 group-hover:text-amber-300 transition-colors" : "text-blaze group-hover:text-volt transition-colors"} size={24} strokeWidth={1.5} />
      <div className={`heading text-xl mt-3 ${admin ? "text-amber-200" : ""}`}>{label}</div>
      <div className={`text-xs mt-1 ${admin ? "text-amber-500/80" : "text-neutral-400"}`}>{sub}</div>
      {admin && <div className="text-[9px] tracking-widest text-amber-500 mt-2 border border-amber-500/40 inline-block px-1.5 py-0.5">ADMIN ONLY</div>}
    </Link>
  );
}

function Shell({ children }) {
  const { user } = useAuth();
  return <div className="min-h-screen bg-appbg text-white">{user?.is_platform_admin ? <AdminNav /> : <Header />}{children}</div>;
}
